import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { SignalData } from "../../types";

interface SignalStrengthIndicatorProps {
  signals?: {
    [gatewayMac: string]: SignalData;
  };
  className?: string;
  mac?: string; // Optional device MAC for debugging/logging
}

const SignalStrengthIndicator: React.FC<SignalStrengthIndicatorProps> = ({
  signals,
  className = "",
  mac,
}) => {
  // Keep the last non-empty signals to avoid flicker when updates temporarily omit signal data
  const [lastSignals, setLastSignals] = useState<{
    [gatewayMac: string]: SignalData;
  } | null>(null);
  const { language } = useSelector((state: RootState) => state.ui);
  const { entities: gateways } = useSelector(
    (state: RootState) => state.gateways
  );
  const [showTooltip, setShowTooltip] = useState(false);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    horizontal: "left" | "right";
    vertical: "top" | "bottom" | "middle";
  }>({ horizontal: "right", vertical: "middle" });
  const [isTooltipMounted, setIsTooltipMounted] = useState(false);
  const [tick, setTick] = useState(0);
  const isHoveringRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cache last non-empty signals so the tooltip doesn't unmount between updates
  React.useEffect(() => {
    if (signals && Object.keys(signals).length > 0) {
      setLastSignals(signals);
    }
  }, [signals]);

  React.useEffect(() => {
    console.info("[SignalIndicator] mount", { mac });
    return () => {
      console.info("[SignalIndicator] unmount", { mac });
    };
  }, [mac]);

  const effectiveSignals = React.useMemo(() => {
    if (signals && Object.keys(signals).length > 0) {
      return signals;
    }
    return lastSignals;
  }, [signals, lastSignals]);

  React.useEffect(() => {
    console.info("[SignalIndicator] signals updated", {
      mac,
      incomingCount: signals ? Object.keys(signals).length : 0,
      cachedCount: lastSignals ? Object.keys(lastSignals).length : 0,
      effectiveCount: effectiveSignals
        ? Object.keys(effectiveSignals).length
        : 0,
    });
  }, [signals, lastSignals, effectiveSignals, mac]);

  // If the pointer is still over the trigger when data changes or the component remounts,
  // force the tooltip to stay open. This guards against re-mounts triggered by parent renders.
  React.useEffect(() => {
    if (containerRef && containerRef.matches(":hover")) {
      isHoveringRef.current = true;
      setIsTooltipMounted(true);
      setShowTooltip(true);
      console.info("[SignalIndicator] re-open due to hover on rerender", {
        mac,
      });
    }
  }, [containerRef, signals]);

  // Update relative timestamps every 30 seconds while tooltip is open
  React.useEffect(() => {
    if (!showTooltip) return;
    const id = setInterval(() => setTick((t) => t + 1), 30000); // 30s
    return () => clearInterval(id);
  }, [showTooltip]);

  // Keep tooltip open across data refreshes while hovered
  React.useEffect(() => {
    if (isHoveringRef.current) {
      setShowTooltip(true);
      setIsTooltipMounted(true);
    }
  }, [signals]);

  // Clear any pending hide timer on unmount
  React.useEffect(
    () => () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    },
    []
  );

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsTooltipMounted(true);
    setShowTooltip(true);
    console.info("[SignalIndicator] mouse enter", { mac });
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        setShowTooltip(false);
        console.info("[SignalIndicator] hide after leave timeout", { mac });
      }
    }, 150); // small delay prevents flicker on rapid re-renders
  };

  React.useEffect(() => {
    console.info("[SignalIndicator] tooltip visibility change", {
      mac,
      showTooltip,
      isTooltipMounted,
      isHovering: isHoveringRef.current,
    });
  }, [showTooltip, isTooltipMounted, mac]);

  // Update tooltip positioning when it becomes visible
  React.useEffect(() => {
    if (showTooltip && containerRef && tooltipRef) {
      const updatePosition = () => {
        const containerRect = containerRef.getBoundingClientRect();
        const tooltipRect = tooltipRef.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const tooltipWidth = tooltipRect.width || 320;
        const tooltipHeight = tooltipRect.height || 200;
        const padding = 20; // Padding from viewport edges

        // Determine horizontal positioning
        let horizontal: "left" | "right" = "right";
        if (containerRect.right + tooltipWidth > windowWidth - padding) {
          horizontal = "left";
        }

        // Determine vertical positioning
        let vertical: "top" | "bottom" | "middle" = "middle";

        // Check if tooltip would overflow bottom
        if (containerRect.bottom + tooltipHeight > windowHeight - padding) {
          // Not enough space below, try positioning above
          vertical = "top";
        } else if (containerRect.top - tooltipHeight < padding) {
          // Not enough space above, position below
          vertical = "bottom";
        } else {
          // Enough space both ways, prefer middle alignment
          vertical = "middle";
        }

        setTooltipPosition({ horizontal, vertical });
      };

      // Update position immediately and on window resize
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [showTooltip, containerRef, tooltipRef]);

  if (!effectiveSignals || Object.keys(effectiveSignals).length === 0) {
    return (
      <span className={`text-gray-400 ${className}`}>
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </span>
    );
  }

  // Convert signals to array and sort by RSSI (strongest first)
  const signalArray = Object.entries(effectiveSignals)
    .map(([gatewayMac, signalData]) => ({
      gatewayMac,
      ...signalData,
      gateway: gateways[gatewayMac],
    }))
    .sort((a, b) => b.rssi - a.rssi);

  // console.log("signalArray", signalArray);

  const activeSignalsArray = signalArray.filter(
    ({ received_at }) =>
      new Date(received_at).getTime() >= Date.now() - 60 * 60 * 1000
  ); // Signals received in the last 1 hour

  // Get the strongest signal from active gateways for overall indicator
  const strongestSignal =
    activeSignalsArray.length > 0 ? activeSignalsArray[0] : signalArray[0];

  // Color coding based on RSSI
  const getSignalColor = (rssi: number) => {
    if (rssi > -50) return "text-green-500";
    if (rssi > -58) return "text-lime-500";
    if (rssi > -65) return "text-yellow-600";
    if (rssi > -75) return "text-amber-600";
    if (rssi > -85) return "text-orange-600";
    return "text-red-600";
  };
  // rssi > -50
  // ? "bg-green-100 text-green-700"
  // : rssi > -58
  // ? "bg-lime-100 text-lime-700"
  // : rssi > -65
  // ? "bg-yellow-100 text-yellow-700"
  // : rssi > -75
  // ? "bg-amber-100 text-amber-700"
  // : rssi > -85
  // ? "bg-orange-100 text-orange-700"
  // : "bg-red-100 text-red-700"

  const getSignalStrength = (rssi: number) => {
    if (rssi > -58) return language === "he" ? "חזק" : "Strong";
    if (rssi > -71) return language === "he" ? "בינוני" : "Medium";
    return language === "he" ? "חלש" : "Weak";
  };

  // Format timestamp to full date/time (DD/MM/YY HH:MM:SS)
  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Format timestamp to relative time
  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const signalTime = new Date(timestamp);
    const diffMinutes = Math.floor(
      (now.getTime() - signalTime.getTime()) / (1000 * 60)
    );

    if (diffMinutes < 1) return language === "he" ? "כעת" : "Now";
    if (diffMinutes < 60) {
      return language === "he"
        ? `לפני ${diffMinutes} דק'`
        : `${diffMinutes}m ago`;
    }
    if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      return language === "he" ? `לפני ${hours} שע'` : `${hours}h ago`;
    }
    const days = Math.floor(diffMinutes / 1440);
    return language === "he" ? `לפני ${days} ימ'` : `${days}d ago`;
  };

  return (
    <div className="relative inline-block" ref={setContainerRef}>
      <div
        className={`cursor-pointer ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`flex items-center space-x-1 ${getSignalColor(
            strongestSignal.rssi
          )}`}
        >
          {/* Signal bars icon */}
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          <span className="text-xs font-medium">{strongestSignal.rssi}dBm</span>
        </div>
      </div>

      {/* Tooltip */}
      {isTooltipMounted && (
        <div
          ref={setTooltipRef}
          className={`absolute z-50 w-80 p-3 bg-white border border-gray-200 rounded-lg shadow-lg transition-all duration-200 ${
            // Horizontal positioning
            tooltipPosition.horizontal === "left"
              ? "right-full mr-2"
              : "left-full ml-2"
          } ${
            // Vertical positioning
            tooltipPosition.vertical === "top"
              ? "bottom-0"
              : tooltipPosition.vertical === "bottom"
              ? "top-0"
              : "top-1/2 -translate-y-1/2"
          } ${
            showTooltip
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-95 pointer-events-none"
          }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Arrow */}
          <div
            className={`absolute w-2 h-2 bg-white border border-gray-200 transform rotate-45 ${
              // Arrow positioning based on tooltip position
              tooltipPosition.vertical === "top"
                ? `top-full -mt-1 ${
                    tooltipPosition.horizontal === "left" ? "right-3" : "left-3"
                  } border-r border-b border-t-0 border-l-0`
                : tooltipPosition.vertical === "bottom"
                ? `bottom-full -mb-1 ${
                    tooltipPosition.horizontal === "left" ? "right-3" : "left-3"
                  } border-l border-t border-r-0 border-b-0`
                : tooltipPosition.horizontal === "left"
                ? "top-3 -right-1 border-t border-r border-b-0 border-l-0"
                : "top-3 -left-1 border-l border-b border-t-0 border-r-0"
            }`}
          ></div>

          <div className="space-y-2">
            <div className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1">
              {language === "he" ? "עוצמת אותות" : "Signal Strengths"}
            </div>

            <div className="space-y-1">
              {activeSignalsArray
                .sort((a, b) => b.rssi - a.rssi) // Sort by best signal (highest RSSI first)
                .map(({ gatewayMac, rssi, received_at, gateway }, index) => (
                  <div
                    key={gatewayMac}
                    className="flex items-center justify-between text-xs py-1"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {gateway?.name || gatewayMac.substring(0, 12)}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {formatDateTime(received_at)} (
                        {getRelativeTime(received_at)})
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${getSignalColor(rssi)}`}>
                        {rssi}dBm
                      </span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${
                          rssi > -50
                            ? "bg-green-100 text-green-700"
                            : rssi > -58
                            ? "bg-lime-100 text-lime-700"
                            : rssi > -65
                            ? "bg-yellow-100 text-yellow-700"
                            : rssi > -75
                            ? "bg-amber-100 text-amber-700"
                            : rssi > -85
                            ? "bg-orange-100 text-orange-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {getSignalStrength(rssi)}
                      </span>
                    </div>
                  </div>
                ))}

              {/* {signalArray.length > activeSignalsArray.length && (
                <div className="text-xs text-gray-500 text-center py-1 border-t border-gray-100">
                  {language === "he"
                    ? `+${
                        signalArray.length - activeSignalsArray.length
                      } שערים נוספים`
                    : `+${
                        signalArray.length - activeSignalsArray.length
                      } more gateways`}
                </div>
              )} */}
            </div>

            {/* <div className="text-xs text-gray-500 text-center pt-1 border-t border-gray-100">
              {language === "he"
                ? `${signalArray.length} שערים זוהו`
                : `${signalArray.length} gateways detected`}
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalStrengthIndicator;
